<template>
    <v-card flat class="glass-background">
        <div class="header">
            <v-row dense class="justify-start">
                <v-col dense cols="auto">
                    <Headers
                        :headersTitle="'Zeitbuchung'"
                        :headersIcon="'mdi-clock-outline'"
                    />
                </v-col>
            </v-row>
        </div>

        <div class="content">
            <div class="filters">
                <v-row dense class="justify-center">
                    <v-col dense cols="auto">
                        <DropDownLocation
                            v-model="selectedLocation"
                            :dropDownLocations="timeBookingCardLocations"
                        />
                    </v-col>
                </v-row>
            </div>

            <div class="checkArea">
                <v-row dense class="justify-center">
                    <v-card class="datePickerCard" elevation="12">
                        <v-row dense class="justify-center mb-2">
                            <v-col dense cols="auto">
                                <DateMenu v-model="dateRange" />
                            </v-col>
                        </v-row>
                        <v-row dense class="justify-center mt-10">
                            <v-date-picker
                                v-model="selectedDates"
                                elevation="12"
                                color="#FF7500"
                                locale="de-DE"
                                first-day-of-week="1"
                                show-adjacent-months
                                multiple
                                :allowedDates="isDateAllowed"
                            >
                                <template #title>Ausgewählte Daten</template>
                                <template #header></template>
                            </v-date-picker>
                        </v-row>

                        <v-row class="justify-center mt-8 pa-2">
                            <v-btn
                                variant="elevated"
                                color="grey"
                                prepend-icon="mdi-help-circle"
                                @click="checkAvailability"
                            >
                                Verfügbarkeit prüfen
                            </v-btn>
                        </v-row>
                    </v-card>
                </v-row>
            </div>
        </div>

        <div class="footer">
            <v-row dense class="justify-center align-center">
                <v-card class="info-Card" elevation="12">
                    <v-row dense class="justify-space-between align-center">

                        <v-col cols="auto">
                            <div class="info-content">
                                <div class="info-text">
                                    Status:
                                    <span class="status-text" :class="statusClass">
                                        {{ statusText }}
                                    </span>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="auto">
                            <v-btn
                                :disabled="!bookable"
                                variant="elevated"
                                color="primary"
                                prepend-icon="mdi-check-circle"
                                @click="bookSeats"
                            >
                                Plätze buchen
                            </v-btn>
                        </v-col>

                    </v-row>
                </v-card>
            </v-row>
        </div>
    </v-card>
</template>

<script>
import Headers from '../consumables/Headers.vue'
import DateMenu from './DateMenu.vue'
import DropDownLocation from '../consumables/DropDownLocation.vue'

export default {
    components: {
        Headers,
        DateMenu,
        DropDownLocation,
    },

    props: {
        timeBookingCardLocations: { type: Object, default: null },
        timeBookingCardUsers: { type: Object, default: null }
    },

    data() {
        return {
            selectedLocation: '',
            selectedDates: [],
            dateRange: { start: '', end: '' },

            bookable: false,
            statusText: "--",
            statusState: "neutral" // "neutral" | "ok" | "error"
        }
    },

    emits: ['updateReservation'],

    computed: {
        statusClass() {
            return {
                'status-ok': this.statusState === 'ok',
                'status-error': this.statusState === 'error',
                'status-neutral': this.statusState === 'neutral',
            }
        }
    },

    watch: {
        dateRange: {
            handler(newVal) {
                if (!newVal.start || !newVal.end) {
                    this.selectedDates = []
                    return
                }

                const start = new Date(newVal.start)
                const end = new Date(newVal.end)
                const days = []
                let current = new Date(start)

                while (current <= end) {
                    const ymd = current.toISOString().split('T')[0]
                    const day = current.getDay()
                    if (day !== 0 && day !== 6) days.push(ymd)
                    current.setDate(current.getDate() + 1)
                }

                this.selectedDates = days
            },
            deep: true
        },

        selectedDates() {
            this.bookable = false
            this.statusText = "--"
            this.statusState = "neutral"
        }
    },

    methods: {
        async checkAvailability() {
            if (!this.selectedDates.length) {
                this.statusText = "--"
                this.statusState = "neutral"
                this.bookable = false
                return
            }

            const sorted = [...this.selectedDates].sort((a, b) => new Date(a) - new Date(b))
            const start = this.formatDateToYMD(sorted[0])
            const end = this.formatDateToYMD(sorted.at(-1))

            try {
                await this.$axios.get("/api/reservations/check-availability/", {
                    params: { start, end, location: this.selectedLocation }
                })

                this.statusText = "Frei"
                this.statusState = "ok"
                this.bookable = true

            } catch (err) {
                this.statusText = "Belegt"
                this.statusState = "error"
                this.bookable = false
            }
        },

        async bookSeats() {
            if (!this.bookable) return

            const sorted = [...this.selectedDates].sort((a, b) => new Date(a) - new Date(b))
            const start = this.formatDateToYMD(sorted[0])
            const end = this.formatDateToYMD(sorted.at(-1))

            try {
                await this.$axios.post("/api/reservations/auto-book/", {
                    start,
                    end,
                    reserved_for: this.timeBookingCardUsers.current_user_id,
                    comment: ""
                })

                this.statusText = "Gebucht"
                this.statusState = "ok"
                this.bookable = false
                this.selectedDates = []
                this.$emit('updateReservation')

            } catch (err) {
                this.statusText = "Fehler"
                this.statusState = "error"
                this.bookable = false
            }
        },

        isDateAllowed(date) {
            const current = this.formatDateToYMD(date)
            const today = new Date()
            const day = new Date(date).getDay()

            if (day === 0 || day === 6) return false

            const maxDate = new Date()
            maxDate.setMonth(maxDate.getMonth() + 3)

            if (new Date(date) > maxDate) return false

            const formattedToday = today.toISOString().split("T")[0]
            if (current < formattedToday) return false

            return true
        },

        formatDateToYMD(date) {
            if (typeof date === 'string') return date
            const d = new Date(date)
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        }
    }
}
</script>

<style scoped>
.glass-background {
    height: 47.75rem;
    max-width: 35rem;
    flex-direction: column;
    background: rgba(var(--v-theme-surface), 0.72);
    backdrop-filter: blur(8px) saturate(120%);
    border: 1px solid rgba(var(--v-theme-surface), 0.35);
    border-radius: 32px;
    overflow: visible;
    padding: 1rem;
}
.header {
    position: relative;
    margin-top: -2rem;
    margin-left: 0.5rem;
    z-index: 10;
    flex-shrink: 0;
}
.content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.filters {
    flex-shrink: 0;
    margin-top: 0.5rem;
}
.checkArea {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.datePickerCard {
    margin-top: 1rem;
    padding: 2rem;
    width: 100%;
    height: 100%;
    border-radius: 16px;
}
.footer {
    flex-shrink: 0;
    margin-top: 1rem;
    padding: 0 !important;
}
.info-Card {
    padding: 0.7rem 1rem;
    border-radius: 16px;
    width: 100%
}
.info-text {
    font-size: 1.25rem;
}
.status-text {
    font-weight: 700;
    margin-left: 0.35rem;
}
.status-ok {
    color: #00b409;
}
.status-error {
    color: #e00000;
}
.status-neutral {
    color: #333;
}
</style>
